import { Box, Modal, Pressable, Spinner, Text } from "native-base";
import React, { useEffect, useMemo, useState } from "react";
import { useWindowDimensions } from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { BackButtonBackHome } from "../Universal/Buttons/BackButtonBackHomeModified";
import { webFont } from "../Universal/API/API";
import { useAppContext } from "../Universal/API/Context-API/AppContext";
import { CollectionsDeanteData, DeanteData } from "../Universal/API/Context-API/AppProvider";
import { formUnit } from "../HomeScreen/HomeScreen";
import { UnitProps } from "../HomeScreen/Components/Unit";
import { ListOfUnitsV3 } from "../HomeScreen/Components/V3";
import { AutoHeighImage } from "../Universal/Image/AutoWidthImage";
import PressableWrapp from "../Universal/Buttons/PressableReact/PressableWrapp";
import CollectionHeaderV2, {
  CollectionTypeSectionV2,
} from "./Components/CollectionHeaderV2";

interface CollectionProps {
  navigation: any;
  route: any;
}

interface CollectionSection {
  key: string;
  label: string;
  items: DeanteData[];
}

const Collection: React.FC<CollectionProps> = ({ navigation, route }) => {
  const {
    scaleAll,
    allUnitData,
    allCollectionsData,
    currentWidth,
    currentHeight,
    isMediumVersion,
    isSmallVersion,
    sortOrderData,
  } = useAppContext();

  const { collection_name } = route.params;
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const isFocused = useIsFocused();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedSectionKey, setSelectedSectionKey] = useState("all");

  const collection = useMemo<CollectionsDeanteData | null>(
    () => (allCollectionsData ? allCollectionsData[collection_name] ?? null : null),
    [allCollectionsData, collection_name]
  );

  const sortOrder = useMemo<string[]>(() => {
    const resolved = sortOrderData;
    return Array.isArray(resolved) ? resolved : [];
  }, [sortOrderData]);

  const norm = React.useCallback((value: unknown) => {
    return (value ?? "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
  }, []);

  const orderIndex = useMemo(() => {
    return new Map(sortOrder.map((name, index) => [norm(name), index]));
  }, [norm, sortOrder]);

  const sortInsideByColor = React.useCallback(
    (items: DeanteData[]) => {
      return items
        .map((unit, originalIndex) => ({ unit, originalIndex }))
        .sort((a, b) => {
          const aColor = norm(a.unit.color1 ?? a.unit.color2 ?? a.unit.color3 ?? "");
          const bColor = norm(b.unit.color1 ?? b.unit.color2 ?? b.unit.color3 ?? "");

          if (!aColor && bColor) return 1;
          if (aColor && !bColor) return -1;

          if (aColor !== bColor) {
            return aColor.localeCompare(bColor, "ru");
          }

          return a.originalIndex - b.originalIndex;
        })
        .map((entry) => entry.unit);
    },
    [norm]
  );

  const collectionUnits = useMemo(() => {
    if (!allUnitData || !collection) return [];

    if (Array.isArray(collection.units_ids) && collection.units_ids.length > 0) {
      return collection.units_ids
        .map((id) => allUnitData[String(id)] ?? null)
        .filter(Boolean) as DeanteData[];
    }

    return Object.values(allUnitData).filter((unit) => unit.col === collection_name);
  }, [allUnitData, collection, collection_name]);

  const sections = useMemo<CollectionSection[]>(() => {
    const groups = new Map<string, { label: string; items: DeanteData[]; firstIndex: number }>();

    collectionUnits.forEach((unit, index) => {
      const key = norm(unit.superType) || "__misc__";
      const existing = groups.get(key);

      if (existing) {
        existing.items.push(unit);
        return;
      }

      groups.set(key, {
        label: unit.superType?.trim?.() || "Прочее",
        items: [unit],
        firstIndex: index,
      });
    });

    return Array.from(groups.entries())
      .sort((a, b) => {
        const aIndex = orderIndex.has(a[0]) ? orderIndex.get(a[0])! : Number.POSITIVE_INFINITY;
        const bIndex = orderIndex.has(b[0]) ? orderIndex.get(b[0])! : Number.POSITIVE_INFINITY;

        if (aIndex !== bIndex) return aIndex - bIndex;

        return a[1].firstIndex - b[1].firstIndex;
      })
      .map(([key, group]) => ({
        key,
        label: group.label,
        items: sortInsideByColor(group.items),
      }));
  }, [collectionUnits, norm, orderIndex, sortInsideByColor]);

  useEffect(() => {
    setSelectedSectionKey("all");
  }, [collection_name]);

  useEffect(() => {
    if (selectedSectionKey === "all") return;

    const hasSelectedSection = sections.some((section) => section.key === selectedSectionKey);

    if (!hasSelectedSection) {
      setSelectedSectionKey("all");
    }
  }, [sections, selectedSectionKey]);

  const selectedSection = useMemo(
    () => sections.find((section) => section.key === selectedSectionKey) ?? null,
    [sections, selectedSectionKey]
  );

  const visibleUnitsData = useMemo(() => {
    if (selectedSectionKey === "all") {
      return sections.flatMap((section) => section.items);
    }

    return selectedSection?.items ?? [];
  }, [sections, selectedSection, selectedSectionKey]);

  const listUnits = useMemo<UnitProps["unit"][]>(
    () => visibleUnitsData.map((unit) => formUnit(unit)),
    [visibleUnitsData]
  );

  const sectionPills = useMemo<CollectionTypeSectionV2[]>(
    () =>
      sections.map((section) => ({
        key: section.key,
        label: section.label,
        count: section.items.length,
      })),
    [sections]
  );

  const selectedSectionLabel = useMemo(() => {
    if (selectedSectionKey === "all") {
      return "Все разделы";
    }

    return selectedSection?.label ?? "Раздел";
  }, [selectedSection, selectedSectionKey]);

  const galleryImages = useMemo(
    () => collection?.collection_full_image_gallery_slides ?? [],
    [collection?.collection_full_image_gallery_slides]
  );

  const handleOpenPdf = React.useCallback(() => {
    if (!collection?.link_pdf || typeof window === "undefined") return;

    window.open(collection.link_pdf, "_blank", "noopener,noreferrer");
  }, [collection?.link_pdf]);

  const handleOpenImage = React.useCallback((image: string) => {
    setSelectedImage(image);
    setIsModalVisible(true);
  }, []);

  const handleAdvanceGalleryImage = React.useCallback(() => {
    if (!selectedImage || !galleryImages.length) return;

    const currentIndex = galleryImages.findIndex((image) => image === selectedImage);

    if (currentIndex === -1) {
      setSelectedImage(galleryImages[0]);
      return;
    }

    setSelectedImage(galleryImages[(currentIndex + 1) % galleryImages.length]);
  }, [galleryImages, selectedImage]);

  const header = useMemo(
    () => (
      <CollectionHeaderV2
        collectionName={collection_name}
        images={galleryImages}
        totalUnitsCount={collectionUnits.length}
        sections={sectionPills}
        selectedSectionKey={selectedSectionKey}
        onSelectSection={setSelectedSectionKey}
        linkPdf={collection?.link_pdf}
        scaleAll={scaleAll}
        isSmallVersion={isSmallVersion}
        isMediumVersion={isMediumVersion}
        onPressImage={handleOpenImage}
        onOpenPdf={handleOpenPdf}
      />
    ),
    [
      collection?.link_pdf,
      collectionUnits.length,
      collection_name,
      galleryImages,
      handleOpenImage,
      handleOpenPdf,
      isMediumVersion,
      isSmallVersion,
      scaleAll,
      sectionPills,
      selectedSectionKey,
    ]
  );

  const listEmptyComponent = useMemo(() => {
    if (!allUnitData) {
      return (
        <Box height={300} justifyContent="center" alignItems="center">
          <Spinner />
        </Box>
      );
    }

    return (
      <Box
        alignSelf="center"
        width="100%"
        maxWidth={960 * scaleAll}
        mx="auto"
        mt={4}
        mb={10}
        px={6}
        py={8}
        borderRadius={30}
        borderWidth={1}
        borderColor="#ECE8E1"
        backgroundColor="#FBF9F5"
      >
        <Text
          selectable={false}
          style={{
            fontFamily: webFont("Inter"),
            fontSize: 28 * scaleAll,
            lineHeight: 34 * scaleAll,
            color: "#111111",
            fontWeight: "700",
            marginBottom: 10 * scaleAll,
          }}
        >
          Ничего не найдено
        </Text>
        <Text
          selectable={false}
          style={{
            fontFamily: webFont("Inter"),
            fontSize: 15 * scaleAll,
            lineHeight: 22 * scaleAll,
            color: "#6B665F",
            fontWeight: "500",
          }}
        >
          В разделе {selectedSectionLabel.toLowerCase()} пока нет позиций. Попробуй
          вернуться ко всем разделам коллекции.
        </Text>
      </Box>
    );
  }, [allUnitData, scaleAll, selectedSectionLabel]);

  return (
    <Box style={{ flex: 1, backgroundColor: "#FFFFFF" }}>
      <BackButtonBackHome navigation={navigation} />

      {isFocused ? (
        <ListOfUnitsV3
          listOfUnis={listUnits}
          navigation={navigation}
          extraUnitParams={{
            fromRoute: "Collection",
            collection_name,
          }}
          renderHeader={header}
          listEmptyComponent={listEmptyComponent}
        />
      ) : null}

      <Modal
        isOpen={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        width={currentWidth}
        height={currentHeight}
        backdropVisible={false}
      >
        <Pressable
          style={{
            flex: 1,
            width: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            justifyContent: "center",
            alignItems: "center",
          }}
          onPress={() => setIsModalVisible(false)}
        >
          <Modal
            isOpen={isModalVisible}
            onClose={() => setIsModalVisible(false)}
            width={screenWidth}
            height={screenHeight}
            backdropVisible={true}
          >
            <Pressable
              style={{
                flex: 1,
                width: "100%",
                backgroundColor: "rgba(0,0,0,0.5)",
                justifyContent: "center",
                alignItems: "center",
              }}
              onPress={() => setIsModalVisible(false)}
            >
              {selectedImage ? (
                <PressableWrapp onPress={handleAdvanceGalleryImage}>
                  <AutoHeighImage
                    width={Math.min(screenWidth * 0.9, 1000)}
                    scaleAll={scaleAll}
                    source={{ uri: selectedImage }}
                    maxHeight={screenHeight}
                  />
                </PressableWrapp>
              ) : null}
            </Pressable>
          </Modal>
        </Pressable>
      </Modal>
    </Box>
  );
};

export default React.memo(Collection);
